'use client';

import {
  AppearanceSelectorOption,
  AppearanceSelector as AppearanceSelectorWrapper,
} from '@sprd/sprd-component-kit/v2';
import clsx from 'clsx';
import { useSetAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import { pdpParamsWithSyncAtom } from '@/atoms/pdpParamsAtom';
import { useResolvedAppearances } from '@/components/ProductCard/AppearanceConfigProvider';
import { tracker } from '@/utility/tracking/tracker';

type AppearanceSelectorProps = {
  productType: ClientProductType;
  selectedAppearanceId: string;
};
export const AppearanceSelector = ({
  productType,
  selectedAppearanceId,
}: AppearanceSelectorProps) => {
  const t = useTranslations();
  const setPdpParams = useSetAtom(pdpParamsWithSyncAtom);
  const appearances = useResolvedAppearances(productType.appearanceIds);

  const handleAppearanceClick = (appearanceId: string) => {
    tracker.trackFeature({
      featureUsed: 'choose-color',
      detail: appearanceId,
    });
    tracker.trackClientProduct({ product: { appearanceId } });

    setPdpParams({ appearanceId });
  };

  return (
    <AppearanceSelectorWrapper
      label={t('cyoAssortmentDetailPage.selectAppearance')}
      className={clsx(
        'flex snap-x flex-nowrap overflow-x-auto overflow-y-hidden md:flex-wrap md:overflow-hidden',
        // To make the slider on mobile stretch
        '-mr-4 sm:-mr-6 md:-mr-0',
        // Padding for the scrollbar on mobile
        'py-2 md:py-0'
      )}
    >
      {appearances.map((appearance) => {
        const appearanceAvailable = productType.stockStates
          .filter((stockState) => stockState.appearance.id === appearance.id)
          .some((stockState) => stockState.available);

        return (
          <AppearanceSelectorOption
            className="relative shrink-0 snap-start"
            key={appearance.id}
            id={appearance.id}
            colors={appearance.colors}
            name={appearance.name}
            size="l"
            selected={appearance.id === selectedAppearanceId}
            textureImage={appearance.textureImage}
            disabled={!appearanceAvailable}
            asChild
          >
            <button onClick={() => handleAppearanceClick(appearance.id)} />
          </AppearanceSelectorOption>
        );
      })}
    </AppearanceSelectorWrapper>
  );
};
